<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import type {
  CardioState, CoreState, ProfileState, PushUpsState, WhtrState,
} from '../lib/types'
import PushUpsAccordion from './PushUpsAccordion.vue'
import CoreAccordion from './CoreAccordion.vue'
import CardioAccordion from './CardioAccordion.vue'
import WHtRAccordion from './WHtRAccordion.vue'
import { SCORING_FIELD_PLACEHOLDER } from '../lib/scoringInput'

interface ComponentScores {
  pu: number | null
  core: number | null
  cardio: number | null
  whtr: number | null
}

defineProps<{
  profile: ProfileState
  pushUps: PushUpsState
  core: CoreState
  cardio: CardioState
  whtr: WhtrState
  componentScores: ComponentScores
  anyExempt: boolean
  ufpmMemberName?: string
  ufpmMemberRank?: string
  hideCalculate?: boolean
  liveComposite?: number | null
  liveCategory?: string | null
  saveDisabled?: boolean
}>()

const emit = defineEmits<{
  close: []
  calculate: []
  save: []
  clear: []
}>()

let previousBodyOverflow = ''

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close')
}

onMounted(() => {
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.body.style.overflow = previousBodyOverflow
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div class="record-sheet" role="dialog" aria-modal="true" aria-labelledby="record-test-title">
      <button class="record-sheet__scrim" type="button" aria-label="Close Record Test" @click="emit('close')" />

      <section class="record-sheet__panel">
        <header class="record-sheet__header">
          <div>
            <div class="record-sheet__eyebrow">PFRA Record Test</div>
            <h2 id="record-test-title">Record Test</h2>
            <div v-if="ufpmMemberName" class="record-sheet__member">
              <span v-if="ufpmMemberRank" class="record-sheet__rank">{{ ufpmMemberRank }}</span>
              <span class="record-sheet__member-name">{{ ufpmMemberName }}</span>
            </div>
          </div>
          <button class="record-sheet__close" type="button" aria-label="Close Record Test" @click="emit('close')">
            x
          </button>
        </header>

        <div class="record-sheet__body">
          <div class="record-sheet__profile">
            <div class="field">
              <label>Sex</label>
              <div class="toggle-group">
                <button :class="['toggle-btn', { active: profile.sex === 'male' }]" @click="profile.sex = 'male'">
                  Male
                </button>
                <button :class="['toggle-btn', { active: profile.sex === 'female' }]" @click="profile.sex = 'female'">
                  Female
                </button>
              </div>
            </div>
            <div class="field sm">
              <label>Age</label>
              <input v-model="profile.age" type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" aria-label="Age" />
            </div>
          </div>

          <div v-show="anyExempt" class="alert alert-info record-sheet__alert">
            Note: Exempted components are excluded from scoring. The composite is calculated from remaining components, redistributed proportionally.
          </div>

          <WHtRAccordion
            v-model:waist="whtr.waist"
            v-model:height="whtr.height"
            v-model:exempt="whtr.exempt"
            :component-score="componentScores.whtr"
          />

          <PushUpsAccordion
            v-model:type="pushUps.type"
            v-model:reps="pushUps.reps"
            v-model:exempt="pushUps.exempt"
            :component-score="componentScores.pu"
          />

          <CoreAccordion
            v-model:type="core.type"
            v-model:reps="core.reps"
            v-model:plankMin="core.plankMin"
            v-model:plankSec="core.plankSec"
            v-model:exempt="core.exempt"
            :component-score="componentScores.core"
          />

          <CardioAccordion
            v-model:type="cardio.type"
            v-model:runMin="cardio.runMin"
            v-model:runSec="cardio.runSec"
            v-model:shuttles="cardio.shuttles"
            v-model:exempt="cardio.exempt"
            :component-score="componentScores.cardio"
          />
        </div>

        <footer class="record-sheet__actions">
          <div v-if="hideCalculate && liveComposite != null" class="record-sheet__live">
            <span class="record-sheet__live-label">Composite</span>
            <span class="record-sheet__live-value">{{ liveComposite.toFixed(1) }}</span>
          </div>
          <button
            v-if="hideCalculate"
            class="btn btn-primary"
            type="button"
            :disabled="saveDisabled"
            @click="emit('save')"
          >Save Score</button>
          <button
            v-else
            class="btn btn-primary"
            type="button"
            @click="emit('calculate')"
          >Calculate Score</button>
          <button class="btn btn-secondary" type="button" @click="emit('clear')">Clear</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.record-sheet {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.record-sheet__scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.58);
  cursor: pointer;
}
.record-sheet__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(100%, 720px);
  height: 100dvh;
  background: var(--bg);
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.46);
}
.record-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: calc(18px + var(--ptr-safe-area-top)) 18px 14px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.record-sheet__eyebrow {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent);
}
.record-sheet__header h2 {
  margin: 2px 0 0;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 30px;
  line-height: 1;
  letter-spacing: 0;
  text-transform: uppercase;
  color: var(--text);
}
.record-sheet__member {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.record-sheet__rank {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--accent);
}
.record-sheet__member-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}
.record-sheet__close {
  width: 44px;
  height: 44px;
  border: 1px solid var(--border2);
  border-radius: 6px;
  background: var(--bg);
  color: var(--text);
  cursor: pointer;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.record-sheet__body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 14px 104px;
}
.record-sheet__profile {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px;
  gap: 12px;
  margin-bottom: 12px;
  padding: 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
}
.record-sheet__alert {
  margin-bottom: 10px;
}
.record-sheet__actions {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px calc(12px + var(--ptr-safe-area-bottom));
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  border-top: 1px solid var(--border);
  box-shadow: 0 -12px 24px rgba(0, 0, 0, 0.24);
}
.record-sheet__live {
  display: flex;
  align-items: baseline;
  gap: 6px;
  width: 100%;
  margin-bottom: 2px;
}
.record-sheet__live-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted, var(--text2));
}
.record-sheet__live-value {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}
.record-sheet__live-cat {
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
}
.record-sheet__actions .btn {
  flex: 1;
  min-height: 46px;
}
.record-sheet :deep(input),
.record-sheet :deep(select),
.record-sheet :deep(textarea),
.record-sheet :deep(button) {
  font-size: max(16px, 1rem);
}
.record-sheet :deep(.acc-header) {
  min-height: 58px;
}
.record-sheet :deep(.acc-exempt input) {
  width: 18px !important;
  height: 18px !important;
}

@media (min-width: 760px) {
  .record-sheet {
    justify-content: flex-end;
  }
  .record-sheet__panel {
    width: min(720px, 92vw);
  }
}

@media (max-width: 480px) {
  .record-sheet__profile {
    grid-template-columns: 1fr;
  }
  .record-sheet__body {
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>
